import { observer } from 'mobx-react';
import React from 'react';

import { GridLegacy as Grid } from '@mui/material';

import dayjs from 'dayjs';

import { formatFloating, formatMoment } from 'utils/formatters/timezone';

import { timezoneStore } from 'stores/Timezone';

import { IDP_FIELDS, UnresolvedValue } from '../IdpManaged';

import EditIcon from '@mui/icons-material/Edit';
import GroupsIcon from '@mui/icons-material/Groups';

import { Button } from 'components/Button';

import GridListRenderer from 'components/Grid/renderers/GridListRenderer';

import { SectionGrid } from 'components/BackgroundInformation/styles';

import Tooltip from 'ui/Tooltip/Tooltip';

import { UuidableName } from 'utils/api/types';

import { colors } from 'theme/colors';

import { useStore } from '../../../../utils/hooks/store';
import useMappedFields from '../../hooks/useMappedFields';
import { StyledRowTitle, RowValue, TabTitle } from './styles';
import EditPersonalInformation from '../EditPersonalInformation';

const Row: React.FC<{ name: string; children: React.ReactNode; id?: string }> = ({
  name,
  children,
  id,
}): React.ReactElement => (
  <>
    <SectionGrid item container xs={2} alignContent="flex-start" id={id} margin="8px 0">
      <StyledRowTitle>{name}</StyledRowTitle>
    </SectionGrid>
    <SectionGrid item container xs={10}>
      {children}
    </SectionGrid>
  </>
);

const TruncatedTextWithTooltip: React.FC<{ text: string; children: React.ReactElement }> = ({
  text,
  children,
}): React.ReactElement => {
  const textRef = React.useRef<HTMLElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  React.useEffect(() => {
    const checkTruncation = (): void => {
      if (textRef.current) {
        const element = textRef.current;
        setIsTruncated(element.scrollWidth > element.clientWidth);
      }
    };

    checkTruncation();
    window.addEventListener('resize', checkTruncation);

    return () => {
      window.removeEventListener('resize', checkTruncation);
    };
  }, [text]);

  const clonedChild = React.cloneElement(children, { ref: textRef });

  if (isTruncated) {
    return (
      <Tooltip title={text} placement="bottom">
        {clonedChild}
      </Tooltip>
    );
  }

  return clonedChild;
};

// core-api serves dateJoined in UTC without its offset, which formatMoment() alone would read as London time
const formatDateJoined = (dateJoined: string): string =>
  timezoneStore.enabled ? formatMoment(dayjs.utc(dateJoined)) : dayjs(dateJoined).format('D MMM YYYY, hh:mm');

export const PersonalInformation: React.FunctionComponent = () => {
  const {
    userDetails: { details },
  } = useStore();

  const [editing, setEditing] = React.useState(false);
  const { unresolvedFor } = useMappedFields();

  if (!details) {
    return null;
  }

  return (
    <>
      <Grid
        container
        style={{ padding: '16px 20px', borderBottom: '1px solid rgba(0, 0, 0, 0.12)', minHeight: '68px' }}
        alignItems="center"
      >
        <SectionGrid item container xs={10} alignContent="flex-start">
          <TabTitle>Personal information</TabTitle>
        </SectionGrid>
        <SectionGrid item container xs={2} justifyContent="end">
          {!editing && (
            <Button size="medium" variant="text" onClick={() => setEditing(true)} disabled={!details.isActive}>
              <EditIcon sx={{ width: 20, height: 20, marginRight: '8px' }} />
              Edit
            </Button>
          )}
        </SectionGrid>
      </Grid>
      {editing ? (
        <EditPersonalInformation setEditing={setEditing} />
      ) : (
        <Grid container marginTop="16px" sx={{ padding: '0px 20px 20px 20px' }} alignItems="center">
          <Row name="Name">
            <RowValue>{details.name || 'N/A'}</RowValue>
          </Row>
          <Row name="Email">
            <RowValue>{details.email || 'N/A'}</RowValue>
          </Row>
          <Row name="Account">
            <RowValue>{details.organisation.name || 'N/A'}</RowValue>
          </Row>
          <Row name="Phone number">
            <RowValue>{details.phoneNumber || 'N/A'}</RowValue>
          </Row>
          <Row name="Date of birth">
            <RowValue>
              {details.personalDetails.birthDate
                ? formatFloating(details.personalDetails.birthDate, 'date', 'MMM D, YYYY')
                : 'N/A'}
            </RowValue>
          </Row>
          <Row name="Employee ID">
            <RowValue>{details.attributes?.employeeId || 'N/A'}</RowValue>
            <UnresolvedValue field={IDP_FIELDS.employeeId} unresolved={unresolvedFor(IDP_FIELDS.employeeId)} />
          </Row>
          <Row name="Memorable word">
            <RowValue>{details.personalDetails.verificationWord || 'N/A'}</RowValue>
          </Row>
          <Row name="Created">
            <RowValue data-testid="user-created-at">
              {details.dateJoined ? formatDateJoined(details.dateJoined) : 'N/A'}
            </RowValue>
          </Row>
          <Row name="Teams">
            {details.groups.length > 0 ? (
              <GridListRenderer
                maxItems={3}
                value={details.groups}
                rendererItem={({ value }) => {
                  const teamName = (value.value as UuidableName).name;
                  return (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        minWidth: 0,
                        flexShrink: 1,
                        maxWidth: '100%',
                      }}
                    >
                      <GroupsIcon sx={{ color: colors.text.secondary, flexShrink: 0 }} />
                      <TruncatedTextWithTooltip text={teamName}>
                        <RowValue
                          style={{
                            margin: '0 16px 0 6px',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            minWidth: 0,
                            flex: 1,
                          }}
                        >
                          {teamName}
                        </RowValue>
                      </TruncatedTextWithTooltip>
                    </div>
                  );
                }}
              />
            ) : (
              <RowValue>N/A</RowValue>
            )}
          </Row>
        </Grid>
      )}
    </>
  );
};

export default observer(PersonalInformation);
